import {
  PLATFORM_INTEGRATION,
  ProductName,
  ProductStatusActivation,
} from '@oy/types';
import {
  GET_PRODUCT_STATUS_URL,
  getProductStatus,
  getProductStatusActivation,
} from 'src/clients';
import useSWR from 'swr';
import { useIntegrationConnectionStatus } from './data/useIntegrationData/useIntegrationConnectionStatus';
import { useUpgradeAccountStatus } from './useUpgradeAccountStatus';

export const useDataActivation = (onlyProduction = false) => {
  const { isProdApproved } = useUpgradeAccountStatus();
  const swr = useSWR(
    isProdApproved ? ['products/status', onlyProduction] : null,
    () => getProductStatusActivation(onlyProduction),
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const result = new Map<ProductName, ProductStatusActivation>(
    swr.data?.data?.data?.map((obj: ProductStatusActivation) => [
      obj.product_name,
      obj,
    ]),
  );

  return { result, ...swr };
};

const useDataBundlingActivation = () => {
  const productStatusBundling = useSWR(
    GET_PRODUCT_STATUS_URL,
    () => getProductStatus(),
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  if (productStatusBundling?.data?.data?.data) {
    return productStatusBundling?.data?.data?.data;
  }

  return [];
};

export const useProductActiveV2 = (onlyProduction = false) => {
  const { result: data, isLoading } = useDataActivation(onlyProduction);
  const dataBundling = useDataBundlingActivation();

  const { connectedTo } = useIntegrationConnectionStatus();
  const { isProdApproved } = useUpgradeAccountStatus();

  const isActivationRequested = (product: ProductName) =>
    data?.get(product)?.has_requested ?? false;

  const checkActive = (product: ProductName) =>
    data?.get(product)?.status == 'ACTIVE';

  const checkStatusKyb = (product: ProductName) =>
    data?.get(product)?.kyb_status;

  const checkActivationStatus = (product: ProductName) => {
    if (onlyProduction && !isProdApproved) return 'INACTIVE';
    if (product in PLATFORM_INTEGRATION) {
      return connectedTo == product ? 'CONNECTED' : 'NOT_CONNECTED';
    }

    const isReviewing = isActivationRequested(product)
      ? 'NEED_REVIEW'
      : 'INACTIVE';

    if (product === 'INTERNATIONAL_TRANSFER') {
      const statusReceiveMoney = dataBundling.find(
        item => item.product === 'DISBURSEMENT',
      )?.status;
      if (statusReceiveMoney === 'NOT_SUBMITTED') {
        return 'INACTIVE';
      }
    }

    return checkActive(product) ? 'ACTIVE' : isReviewing;
  };

  return {
    checkActive,
    checkActivationStatus,
    checkStatusKyb,
    isCheckActiveLoading: isLoading,
    dataBundling,
  };
};
